<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { DEFAULT_LOCALE } from '@/i18n'
import { staticPageSlugs, staticPageHref, type ProductKey } from '@/router/products'
import { subscriptionFlowFor } from '@/subscriptionFlows'
import { useSubscriptionState } from '@/composables/useSubscriptionState'
import { useSubscriptionOffer, type RecapViewCard } from '@/composables/useSubscriptionOffer'
import { useSubscriptionFinalize } from '@/composables/useSubscriptionFinalize'
import { usePaymentLink } from '@/composables/usePaymentLink'
import { useAuth } from '@/composables/useAuth'
import type { CheckoutMode, SubscriberInfo } from '@/services/subscription'
import { useCommuneAutocomplete, type CommuneSuggestion } from '@/composables/useCommuneAutocomplete'
import type { CommuneValidationError } from '@/services/communeSearch'
import {
  COUNTRY_CODES_ADDRESS,
  COUNTRY_CODES_PHONE,
  DEFAULT_COUNTRY,
  COUNTRY_DIAL_CODES,
  flagEmoji,
  countryLabel,
  type AddressCountryCode,
  type PhoneCountryCode,
} from '@/data/countries'
import { formatSsn, normalizeSsn, validateSsn, SSN_ERROR_KEYS } from '@/utils/ssn'
import { normalizeBank, validateIban, validateBic, type IbanError, type BicError } from '@/utils/bank'
import { formatPhoneAsYouType, isPhoneTooLong, validatePhone, toE164, fromE164 } from '@/utils/phone'
import { birthYearRange as birthYearRangeFor, validateBirthDate } from '@/utils/birthDate'
import { scrollToTop } from '@/utils/scroll'

const props = defineProps<{
  productKey?: ProductKey
}>()

/** Activation de la phase « Vos informations ». v-model:active — InfoForm la
 *  désactive lui-même (retour navigateur / navigation hors arbre) et le parent
 *  la lit pour masquer ses propres écrans (chaîne gated sur !active). */
const active = defineModel<boolean>('active', { default: false })
/** Sous-étape courante (1..récap). v-model:step — exposée pour le stepper du parent. */
const infoStep = defineModel<number>('step', { default: 1 })

const { t } = useI18n()
const router = useRouter()
const { answers, currentStepKey, locale } = useSubscriptionState()
// Espace connecté : les informations personnelles du formulaire sont préremplies
// depuis le profil /me (voir maybePrefillFromMember). Générique → tous parcours.
const { member, isAuthenticated, memberLoaded } = useAuth()
const isConnected = computed(() => isAuthenticated.value)

/** En espace connecté, les champs de « Vos informations » sont en lecture seule :
 *  ils reflètent le profil backend (l'utilisateur est identifié par user_id), et
 *  restent verrouillés même si /me ne renvoie pas de valeur pour ce champ. Le
 *  paramètre `value` est conservé pour la lisibilité des appels (le champ visé)
 *  mais n'entre plus dans la décision. */
function fieldLocked(_value?: unknown): boolean {
  return isConnected.value
}

// Offre retenue + récap de finalisation (mêmes view-models que les écrans
// « offre sur-mesure » du parent — source unique dans useSubscriptionOffer).
const {
  // Age limits of the journey (product × self/child). They drive both the list of
  // selectable birth years and the validation of the entered date — see
  // @/utils/birthDate, which is what keeps the two guards in step.
  ageBounds,
  // Source de vérité unique (bundle : assia_forwarding === 'automatic') pour la
  // collecte des champs RIB + NIR : pilote affichage, validation, payload et
  // nombre d'étapes — cohérence garantie par ce seul computed.
  assiaForwardingAutomatic,
  infoStepCount,
  infoRecapStep,
  recapView,
  formatPrice,
} = useSubscriptionOffer(() => props.productKey)

/* ----------------------------------------------------------------
 * Phase « Vos informations » (4 sous-étapes · depenses-sante).
 * État local au composant : ces données sont sensibles, on ne les expose
 * pas dans l'URL. Le titre affiche {step}/4 ; seule l'étape 1 est câblée
 * pour l'instant, les suivantes sont un placeholder à construire.
 * ----------------------------------------------------------------*/
const infoSubmitted = ref(false)
const info = reactive({
  civility: '' as '' | 'mr' | 'mrs',
  lastName: '',
  firstName: '',
  birthDay: null as number | null,
  birthMonth: null as number | null,
  birthYear: null as number | null,
  country: DEFAULT_COUNTRY as AddressCountryCode,
  address: '',
  addressComplement: '',
  city: '',
  postcode: '',
  communeCode: '',
  email: '',
  emailConfirm: '',
  phoneCountry: DEFAULT_COUNTRY as PhoneCountryCode,
  phone: '',
  ssn: '',
  noSsn: false,
  iban: '',
  bic: '',
  ibanHolder: '',
  ibanLater: false,
  paymentMode: 'upfront' as 'upfront' | 'monthly',
  // Prospecting opt-in: always starts unticked, connected space included — the
  // consent is asked for again on every journey rather than mirroring /me.
  prospectingOptIn: false,
  termsAccepted: false,
  termsExpanded: false,
})

/** Indicatif téléphonique courant — dépend du pays sélectionné pour le téléphone
 *  (distinct du pays de l'adresse : un étudiant peut résider en France avec un
 *  numéro étranger, ou l'inverse). */
const phoneDialCode = computed(() => COUNTRY_DIAL_CODES[info.phoneCountry] ?? '')

/** Tooltip natif pour le select indicatif : nom de pays + code (le select
 *  fermé n'affichant que drapeau + indicatif pour rester compact). */
const phoneCountryTitle = computed(
  () => `${countryLabel(info.phoneCountry, locale.value)} (${phoneDialCode.value})`,
)

/** Options du select indicatif : drapeau + nom localisé + indicatif. Trié par
 *  label localisé avec France épinglée en première position (cohérent avec
 *  countryOptions du step 2). */
const phoneCountryOptions = computed(() => {
  const collator = new Intl.Collator(locale.value, { sensitivity: 'base' })
  const labelled = COUNTRY_CODES_PHONE.map((code) => ({
    code,
    name: countryLabel(code, locale.value),
    flag: flagEmoji(code),
    dial: COUNTRY_DIAL_CODES[code],
  }))
  const fr = labelled.find((c) => c.code === DEFAULT_COUNTRY)
  const rest = labelled
    .filter((c) => c.code !== DEFAULT_COUNTRY)
    .sort((a, b) => collator.compare(a.name, b.name))
  return fr ? [fr, ...rest] : rest
})

/** Caractères « significatifs » d'un NIR (chiffres + A/B des départements
 *  corses), seuls comptés pour repositionner le curseur après formatage. */
const SSN_SIGNIFICANT_RE = /[0-9AB]/i

/** Index, dans une valeur formatée, juste après le `n`-ième caractère
 *  significatif — pour replacer le curseur au bon endroit malgré les espaces
 *  insérés par le formatage. */
function ssnCaretAfter(formatted: string, n: number): number {
  if (n <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (SSN_SIGNIFICANT_RE.test(formatted[i]) && ++seen === n) return i + 1
  }
  return formatted.length
}

/** Saisie du NIR : on réécrit la valeur formatée dans le state. Le formatage
 *  insère des espaces, ce qui ferait sauter le curseur en fin de champ ; on le
 *  réancre après le même nombre de caractères significatifs qu'avant la frappe. */
function onSsnInput(event: Event) {
  const input = event.target as HTMLInputElement
  const caret = input.selectionStart ?? input.value.length
  const significantBeforeCaret = input.value.slice(0, caret).replace(/[^0-9AB]/gi, '').length
  const formatted = formatSsn(input.value)
  info.ssn = formatted
  // Resync the DOM value + caret synchronously: Vue won't re-patch an unchanged
  // `:value`, so the caret we set here is preserved.
  input.value = formatted
  const nextCaret = ssnCaretAfter(formatted, significantBeforeCaret)
  input.setSelectionRange(nextCaret, nextCaret)
}

/** Case « je n'ai pas de NIR » : on vide le champ quand elle est cochée pour
 *  ne pas conserver une saisie partielle invisible (le champ est alors désactivé). */
function onNoSsnToggle() {
  if (info.noSsn) info.ssn = ''
}

/** Email basique : présence d'un @ et d'un point dans la partie domaine,
 *  pas de strict RFC. Suffisant pour bloquer les saisies évidemment fausses
 *  sans exclure de vrais emails atypiques. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** Position du curseur exprimée en nombre de chiffres : on compte les chiffres
 *  à gauche du curseur, puis on retrouve l'index correspondant dans la chaîne
 *  reformatée — les séparateurs ajoutés/retirés ne décalent ainsi pas le curseur. */
function countDigits(text: string): number {
  return (text.match(/\d/g) ?? []).length
}

/** Index, dans `formatted`, situé juste après le `digitCount`-ième chiffre. */
function caretAfterDigit(formatted: string, digitCount: number): number {
  if (digitCount <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      seen += 1
      if (seen === digitCount) return i + 1
    }
  }
  return formatted.length
}

/** Reformate le numéro pendant la frappe selon le pays sélectionné (AsYouType),
 *  puis met à jour `info.phone` avec la valeur formatée. La saisie est refusée
 *  dès qu'elle dépasse la longueur max valide du pays : on restaure alors la
 *  dernière valeur acceptée dans l'input (le `:value` ne le ferait pas seul,
 *  `info.phone` n'ayant pas changé).
 *
 *  Le reformatage réécrit la valeur via le binding `:value`, ce qui renverrait
 *  le curseur en fin de champ. On préserve donc sa position : on mémorise le
 *  nombre de chiffres à sa gauche avant reformatage et on le replace au même
 *  rang de chiffre une fois le DOM mis à jour. */
function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement
  if (isPhoneTooLong(target.value, info.phoneCountry)) {
    target.value = info.phone
    return
  }
  const caret = target.selectionStart ?? target.value.length
  const digitsBeforeCaret = countDigits(target.value.slice(0, caret))
  const formatted = formatPhoneAsYouType(target.value, info.phoneCountry)
  info.phone = formatted
  nextTick(() => {
    const pos = caretAfterDigit(formatted, digitsBeforeCaret)
    target.setSelectionRange(pos, pos)
  })
}

// Changer de pays reformate le numéro déjà saisi à la convention du nouveau pays.
watch(() => info.phoneCountry, () => {
  if (info.phone) info.phone = formatPhoneAsYouType(info.phone, info.phoneCountry)
})

/** Clés i18n des messages d'erreur IBAN/BIC, par code retourné par les
 *  validateurs de `@/utils/bank` (l'i18n reste dans la couche UI). */
const IBAN_ERROR_KEYS: Record<IbanError, string> = {
  required: 'subscription.info.errors.iban',
  format: 'subscription.info.errors.ibanInvalid',
  checksum: 'subscription.info.errors.ibanChecksum',
  sepa: 'subscription.info.errors.ibanSepa',
}
const BIC_ERROR_KEYS: Record<BicError, string> = {
  required: 'subscription.info.errors.bic',
  format: 'subscription.info.errors.bicInvalid',
}

/** Liste des pays pour l'adresse — exhaustive ISO 3166-1 alpha-2. Triée par
 *  label localisé via Intl.DisplayNames + fallback pour les codes
 *  exceptionnellement réservés (Ascension, Kosovo, etc.). France épinglée en
 *  première position pour faciliter la sélection par défaut. */
const countryOptions = computed(() => {
  const collator = new Intl.Collator(locale.value, { sensitivity: 'base' })
  const labelled = COUNTRY_CODES_ADDRESS.map((code) => ({
    code,
    label: countryLabel(code, locale.value),
  }))
  const fr = labelled.find((c) => c.code === DEFAULT_COUNTRY)
  const rest = labelled
    .filter((c) => c.code !== DEFAULT_COUNTRY)
    .sort((a, b) => collator.compare(a.label, b.label))
  return fr ? [fr, ...rest] : rest
})

const isCountryFrance = computed(() => info.country === 'FR')

/* --- Autocomplete commune française (ville/CP) dans un seul champ.
 *  Ne tire que lorsque country=FR (cf. geo.api.gouv.fr). Pour les autres pays,
 *  ville et CP sont saisis librement (2 inputs séparés, sans validation commune).
 *  La sélection (commune.selected) est la source de vérité « commune réelle » :
 *  on y miroir info.city / info.postcode / info.communeCode. */
const commune = useCommuneAutocomplete()
const cityPostcodeAsyncError = ref<string | null>(null)

const cityPostcodeModel = computed({
  get: () => commune.query.value,
  set: (v: string) => {
    commune.query.value = v
    commune.open.value = true
    cityPostcodeAsyncError.value = null
  },
})

/** Mappe le code d'erreur de validation commune vers sa clé i18n. */
const COMMUNE_ERROR_KEYS: Record<CommuneValidationError, string> = {
  'commune-not-found': 'communeNotFound',
  'postal-not-found': 'postalNotFound',
  'city-postal-mismatch': 'cityPostalMismatch',
}

// Miroir de la sélection vers le state info (FR uniquement : en mode étranger,
// ville/CP sont des champs libres qu'on ne doit pas écraser).
watch(
  () => commune.selected.value,
  (sel) => {
    if (!isCountryFrance.value) return
    info.city = sel?.city ?? ''
    info.postcode = sel?.postalCode ?? ''
    info.communeCode = sel?.communeCode ?? ''
  },
)

function pickCommune(s: CommuneSuggestion) {
  commune.select(s)
  cityPostcodeAsyncError.value = null
}

/** Nom aléatoire pour le champ autocomplete ville/CP. Objectif : empêcher les
 *  navigateurs (Chrome notamment) de proposer des valeurs précédemment saisies
 *  dans ce champ — ces suggestions sont indésirables car la valeur valide
 *  *doit* venir d'une sélection BAN, pas d'une saisie libre mémorisée. La
 *  randomisation invalide la mémoire per-name du navigateur. */
const cityFieldName = `city-${Math.random().toString(36).slice(2, 10)}`

/** Bascule pays manuelle : on remet à zéro la zone ville/CP pour éviter
 *  d'envoyer une commune française avec un autre pays sélectionné (ou inversement).
 *  Branché sur @change du <select> plutôt qu'un watch pour ne pas effacer les
 *  valeurs restaurées par la persistance sessionStorage au montage. */
function onCountryChange() {
  info.city = ''
  info.postcode = ''
  info.communeCode = ''
  commune.query.value = ''
  commune.reset()
  cityPostcodeAsyncError.value = null
}

/** Birth years offered in the year `<select>`, derived from the journey's age
 *  limits and the bracket picked in the decision tree. The "young" bracket never
 *  reaches this form: the minor exit screen stops it upstream. */
const birthYearRange = computed(() =>
  birthYearRangeFor(ageBounds.value, answers.value.age, new Date()),
)

const birthYearOptions = computed(() => {
  const { min, max } = birthYearRange.value
  const out: number[] = []
  for (let y = max; y >= min; y--) out.push(y)
  // Année préremplie depuis /me (verrouillée) : on la garde affichable même si
  // elle sort de la plage du bucket d'âge courant, sinon le <select> resterait
  // vide alors que jour et mois sont renseignés.
  const prefilled = info.birthYear
  if (prefilled != null && !out.includes(prefilled)) {
    out.push(prefilled)
    out.sort((a, b) => b - a)
  }
  return out
})

/** Noms de mois localisés via Intl (pas de duplication dans i18n). */
const monthsList = computed(() => {
  const fmt = new Intl.DateTimeFormat(locale.value, { month: 'long' })
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: fmt.format(new Date(2000, i, 1)),
  }))
})

/**
 * Localized birth-date error, or `''` when the date is accepted.
 *
 * The rules themselves live in @/utils/birthDate and return a stable code; this
 * only maps it to copy. The two age-limit codes interpolate the journey's own
 * bound, so a single pair of strings covers every product (15 or 18 minimum,
 * 30 or 34 maximum) instead of one message per product.
 */
function birthDateErrorMessage(): string {
  const code = validateBirthDate(
    { day: info.birthDay, month: info.birthMonth, year: info.birthYear },
    { bounds: ageBounds.value, bucket: answers.value.age, today: new Date() },
  )
  if (!code) return ''
  if (code === 'minAge') return t('subscription.info.errors.birthDateMinAge', { min: ageBounds.value.min })
  if (code === 'maxAge') return t('subscription.info.errors.birthDateMaxAge', { max: ageBounds.value.max })
  // required | invalid | future | bucket: fixed wording, no bound to inject.
  const KEYS = { required: 'Required', invalid: 'Invalid', future: 'Future', bucket: 'Bucket' } as const
  return t(`subscription.info.errors.birthDate${KEYS[code]}`)
}

const infoErrors = computed<Record<string, string>>(() => {
  if (!infoSubmitted.value) return {}
  const e: Record<string, string> = {}

  if (infoStep.value === 1) {
    if (!info.civility) e.civility = t('subscription.info.errors.civility')
    // String() + ?? '' protège contre une persistance corrompue (null/undefined)
    // qui ferait planter silencieusement le .trim() à l'intérieur de submitInfoStep.
    if (!String(info.lastName ?? '').trim()) e.lastName = t('subscription.info.errors.lastName')
    if (!String(info.firstName ?? '').trim()) e.firstName = t('subscription.info.errors.firstName')

    const birthDateError = birthDateErrorMessage()
    if (birthDateError) e.birthDate = birthDateError
  } else if (infoStep.value === 2) {
    if (!info.country) e.country = t('subscription.info.errors.country')
    if (!String(info.address ?? '').trim()) e.address = t('subscription.info.errors.address')
    if (isCountryFrance.value) {
      // En mode FR, le champ unique ne doit pas être vide ; la vérification que la
      // valeur correspond à une commune réelle est faite en asynchrone dans
      // submitInfoStep (sélection d'une suggestion ou validation finale via l'API).
      if (!String(commune.query.value ?? '').trim()) {
        e.cityPostcode = t('subscription.info.errors.cityPostcodeFr')
      }
    } else {
      if (!String(info.city ?? '').trim()) e.city = t('subscription.info.errors.city')
      if (!String(info.postcode ?? '').trim()) e.postcode = t('subscription.info.errors.postcode')
    }
  } else if (infoStep.value === 3) {
    const email = String(info.email ?? '').trim()
    if (!email || !EMAIL_RE.test(email)) {
      e.email = t('subscription.info.errors.email')
    } else if (!isConnected.value && String(info.emailConfirm ?? '').trim() !== email) {
      // Confirmation d'email masquée en espace connecté (email non modifiable) :
      // on n'exige pas la correspondance.
      e.emailConfirm = t('subscription.info.errors.emailConfirm')
    }

    // Téléphone facultatif : on ne valide le format (selon le pays) que s'il est
    // renseigné.
    if (validatePhone(String(info.phone ?? ''), info.phoneCountry)) {
      e.phoneInvalid = t('subscription.info.errors.phoneInvalid')
    }

    // NIR : collecté quand le bundle choisi a assia_forwarding === 'automatic'.
    if (assiaForwardingAutomatic.value && !info.noSsn) {
      const ssnCode = validateSsn(String(info.ssn ?? ''))
      if (ssnCode) e.ssn = t(SSN_ERROR_KEYS[ssnCode])
    }
  } else if (infoStep.value === 4 && assiaForwardingAutomatic.value) {
    // RIB/IBAN : collecté quand le bundle choisi a assia_forwarding === 'automatic'.
    if (!info.ibanLater) {
      // Validité de l'IBAN (format + clé mod-97 + zone SEPA, cf. @/utils/bank).
      const ibanCode = validateIban(String(info.iban ?? ''))
      if (ibanCode) e.iban = t(IBAN_ERROR_KEYS[ibanCode])

      const bicCode = validateBic(String(info.bic ?? ''))
      if (bicCode) e.bic = t(BIC_ERROR_KEYS[bicCode])

      if (!String(info.ibanHolder ?? '').trim()) {
        e.ibanHolder = t('subscription.info.errors.ibanHolder')
      }
    }
  }
  return e
})

async function submitInfoStep() {
  infoSubmitted.value = true
  cityPostcodeAsyncError.value = null
  if (Object.keys(infoErrors.value).length > 0) return

  // Validation finale de la commune (FR) : une suggestion sélectionnée est
  // acceptée d'office, sinon la valeur libre est vérifiée auprès de l'API.
  if (infoStep.value === 2 && isCountryFrance.value) {
    const err = await commune.validate()
    if (err) {
      cityPostcodeAsyncError.value = t(`subscription.info.errors.${COMMUNE_ERROR_KEYS[err]}`)
      return
    }
  }

  if (infoStep.value < infoRecapStep.value) {
    // Avancée d'une sous-étape : pas de pushState ici. Une seule entrée « piège »
    // est maintenue (ancrée à l'ouverture / l'hydratation) ; le « Précédent »
    // décrémente puis ré-ancre dans onInfoPopState. Empiler une entrée par
    // sous-étape créerait des entrées figées à la langue d'alors, que la bascule
    // de langue piège ensuite (cf. bloc « Back navigateur »).
    infoStep.value++
    infoSubmitted.value = false
  }
}

// Finalisation du récap (parcours branchés au back). Le contract_draft est créé
// une seule fois par configuration puis réutilisé par les deux actions : Payer
// (ouverture d'une session Stripe + redirection) et Partager. `submitting`/`error`
// sont l'état partagé affiché sous les boutons. Les parcours non branchés
// retombent sur la thank-you page directe.
const {
  pay,
  shareLink,
  submitting: finalizing,
  redirecting,
  error: finalizeError,
} = useSubscriptionFinalize()

// Partager un lien de paiement à un proche : le contract_draft (créé côté récap)
// est transmis par son id à `usePaymentLink`, puis on navigue — la génération du
// lien Stripe se fait à l'ouverture de la page dédiée.
const { prepare: preparePaymentLink } = usePaymentLink()

/** Lien localisé vers la page de connexion (erreur « email déjà utilisé »). */
const loginHref = computed(() => staticPageHref('login', locale.value))

/* --- Libellés de prix du récap générique (RecapView) ----------------------
 * 'stay' : texte précalculé « X € pour <séjour> ». 'duration' : par mois si
 * paiement mensuel, sinon « X € pour N mois ». Centralisé ici pour la carte et
 * le total ; le choix de mode (stay/duration) vient de la stratégie du flow. */
function durationPriceText(total: number | null, monthly: number | null, count: number | null): string {
  if (total === null || !count) return ''
  return effectivePaymentMode.value === 'monthly' && monthly !== null
    ? t('subscription.info.finalRecap.priceMonthly', { price: formatPrice(monthly) })
    : t('subscription.info.finalRecap.priceForDuration', { price: formatPrice(total), count })
}

// Mode de paiement réellement appliqué : le mensuel ne compte que si le parcours
// courant l'offre (monthlyAvailable). Le choix radio (info.paymentMode) est
// partagé entre parcours via le composant réutilisé ; sans ce garde-fou, un
// mensuel choisi ailleurs « fuite » vers un flow paiement unique (showPaymentSelector
// à false), où le total doit toujours refléter la durée choisie.
const effectivePaymentMode = computed<'upfront' | 'monthly'>(() =>
  recapView.value?.monthlyAvailable && info.paymentMode === 'monthly' ? 'monthly' : 'upfront',
)

/** Prix affiché sur une carte du récap. */
function cardPriceText(card: RecapViewCard): string {
  const v = recapView.value
  if (!v) return ''
  return v.priceMode === 'stay'
    ? v.stayText
    : durationPriceText(card.periodTotal, card.periodMonthly, v.durationCount)
}

/** Total affiché en bas du récap. */
const recapTotalText = computed(() => {
  const v = recapView.value
  if (!v) return ''
  return v.priceMode === 'stay'
    ? v.stayText
    : durationPriceText(v.total, v.totalMonthly, v.durationCount)
})

// Si le paiement mensuel devient indisponible (durée < 2 mois) alors qu'il était
// sélectionné, on rebascule sur le paiement en une fois.
watch(
  () => recapView.value?.monthlyAvailable,
  (available) => {
    if (available === false && info.paymentMode === 'monthly') info.paymentMode = 'upfront'
  },
)

/** Format ISO (YYYY-MM-DD) de la date de naissance saisie, ou null si incomplète. */
function birthdateIso(): string | null {
  const { birthYear: y, birthMonth: m, birthDay: d } = info
  if (!y || !m || !d) return null
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

/** Construit les infos souscripteur normalisées (contrat back) depuis le
 *  formulaire. Retourne null si une donnée indispensable manque (garde-fou :
 *  la validation par sous-étape l'empêche normalement). */
function toSubscriberInfo(): SubscriberInfo | null {
  if (!info.civility) return null
  const birthdate = birthdateIso()
  if (!birthdate) return null

  // Numéro au format E.164 (ex. « +33612345678 »), null si vide ou invalide.
  const phoneE164 = info.phone.trim() ? toE164(info.phone, info.phoneCountry) : null
  const ssn = normalizeSsn(info.ssn)
  const iban = normalizeBank(info.iban)

  return {
    civility: info.civility,
    firstName: info.firstName.trim(),
    lastName: info.lastName.trim(),
    birthdate,
    email: info.email.trim(),
    address: {
      address_line1: info.address.trim(),
      ...(info.addressComplement.trim() ? { address_line2: info.addressComplement.trim() } : {}),
      zipcode: info.postcode.trim(),
      city: info.city.trim(),
      country: info.country,
    },
    ...(phoneE164 ? { phoneNumber: phoneE164 } : {}),
    // Unticked means an explicit refusal: the backend expects a boolean, both in
    // the guest contract_draft and on PATCH /users.
    prospectingOptIn: info.prospectingOptIn,
    // Gate sur le parcours courant, pas seulement sur la présence d'une valeur :
    // une valeur résiduelle en mémoire (persistance d'un parcours précédent) ne
    // doit jamais fuiter dans le contract_draft d'un parcours qui ne collecte pas
    // ce champ (ex. solidariteExam n'a ni NIR ni RIB).
    ...(assiaForwardingAutomatic.value && !info.noSsn && ssn ? { socialSecurityNumber: ssn } : {}),
    ...(assiaForwardingAutomatic.value && !info.ibanLater && iban
      ? {
          banking: {
            owner_fullname: info.ibanHolder.trim(),
            iban,
            bic: normalizeBank(info.bic),
          },
        }
      : {}),
  }
}

/** CTA Payer du récap final. Santé : on persiste le contrat puis on redirige
 *  vers Stripe. Autres parcours (pas encore branchés) : thank-you page directe
 *  (slug localisé centralisé dans `staticPageSlugs.thankYou`). */
function submitPayment() {
  if (!info.termsAccepted) return
  // Flow-wired products (health, abroad, …) finalize via the backend: persist
  // the contract draft then redirect to Stripe (handled in useSubscriptionFinalize).
  // Products not yet wired to a flow still fall back to the thank-you page.
  if (subscriptionFlowFor(props.productKey)) {
    const subscriber = toSubscriberInfo()
    if (!subscriber) return
    const mode: CheckoutMode = effectivePaymentMode.value === 'monthly' ? 'subscription' : 'one_time_payment'
    pay(subscriber, mode)
    return
  }
  const slug = staticPageSlugs.thankYou[locale.value]
  router.push(locale.value === DEFAULT_LOCALE ? `/${slug}` : `/${locale.value}/${slug}`)
}

/** CTA « Partager le lien de paiement » du récap final. Comme le paiement, il
 *  exige les conditions acceptées et un parcours branché ; on crée (ou réutilise)
 *  le contract_draft, on transmet son id + le mode (identique au paiement) à
 *  `usePaymentLink`, puis on ouvre la page dédiée qui génère et affiche le lien.
 *  Une erreur de création est affichée sous les boutons via `finalizeError`. */
async function sharePaymentLink() {
  if (!info.termsAccepted) return
  if (!subscriptionFlowFor(props.productKey)) return
  const subscriber = toSubscriberInfo()
  if (!subscriber) return
  const mode: CheckoutMode = effectivePaymentMode.value === 'monthly' ? 'subscription' : 'one_time_payment'
  const draftId = await shareLink(subscriber)
  if (!draftId) return
  preparePaymentLink(draftId, mode)
  router.push(staticPageHref('paymentLink', locale.value))
}

/* ----------------------------------------------------------------
 * Back navigateur dans le formulaire infos.
 * Toutes les sous-étapes (+ le récap-finalisation) partagent la même URL
 * (.../souscrire/securite). Sans intervention, un clic sur « Back » sortait
 * directement vers l'URL précédente (cross-sell), sautant toutes les sous-étapes.
 *
 * Modèle : une SEULE entrée d'historique synthétique « piège » est maintenue au
 * sommet de la pile (via pushState, même URL). À chaque popstate on décrémente
 * infoStep d'exactement un cran et on ré-ancre un nouveau piège, jusqu'à la 1re
 * sous-étape où le « Précédent » sort du formulaire.
 *
 * On NE lit PAS le numéro de sous-étape stocké dans l'entrée d'historique : il se
 * fige dès qu'un changement de langue intervient. La bascule FR↔EN passe par
 * router.replace, et le guard « langue collante » (main.ts) ré-aligne toute URL
 * d'une autre langue sur la langue préférée. Décrémenter inconditionnellement (au
 * lieu de relire le step stocké) garantit donc la progression malgré ce step figé,
 * et on ré-ancre le piège à l'URL du récap dans la langue ACTIVE
 * (router.currentRoute) plutôt qu'à window.location.href, qui pointe
 * transitoirement vers l'entrée périmée d'une autre langue.
 * ----------------------------------------------------------------*/
const INFO_HISTORY_SENTINEL = 'smerra-info'

function pushInfoHistory(step: number) {
  if (typeof window === 'undefined') return
  // Preserve vue-router's history state (back/current/forward/position/scroll)
  // and only add our sentinel. Replacing it wholesale would strip those fields,
  // and the next vue-router navigation would build a broken URL from the now
  // missing `current` (SecurityError on replaceState).
  // URL = récap dans la langue active : router.currentRoute reflète encore la
  // route pré-popstate pendant le handler synchrone (vue-router ne confirme la
  // navigation qu'ensuite), donc la langue préférée même quand window.location
  // pointe momentanément vers une entrée périmée d'une autre langue.
  history.pushState(
    { ...history.state, ji: INFO_HISTORY_SENTINEL, step },
    '',
    window.location.origin + router.currentRoute.value.fullPath,
  )
}

function onInfoPopState() {
  if (!active.value) return
  if (infoStep.value > 1) {
    // Descente d'exactement une sous-étape + ré-ancrage d'un nouveau piège, pour
    // que le prochain « Précédent » descende encore. On calcule l'étape cible en
    // local : un set de defineModel lié au v-model parent n'est pas relu de façon
    // synchrone, donc relire infoStep.value pousserait l'ancienne valeur.
    const prevStep = infoStep.value - 1
    infoStep.value = prevStep
    infoSubmitted.value = false
    pushInfoHistory(prevStep)
  } else {
    // 1re sous-étape : le « Précédent » sort du formulaire (vue-router reprend le
    // relais sur le changement d'URL sous-jacent).
    active.value = false
    infoStep.value = 1
    infoSubmitted.value = false
  }
}

/** Persistance sessionStorage : survit au F5, oubli à la fermeture de l'onglet.
 *  Hydratation dans onMounted (SSR-safe) ; watch côté client uniquement.
 *  La clé inclut une version : on incrémente quand on ajoute des champs à
 *  `info` pour invalider une persistance avec un schema antérieur (qui peut
 *  contenir des valeurs typées différemment et faire planter la validation). */
// Clé propre au produit : les données du formulaire (dont NIR et RIB) ne doivent
// jamais fuiter d'un parcours à l'autre. Sans ce scope, un parcours abandonné
// (ex. complémentaire santé) réhydratait son NIR/RIB dans le formulaire d'un
// autre parcours (ex. solidariteExam) et les faisait partir dans son
// contract_draft. Le numéro de version invalide un schema `info` antérieur.
const SS_INFO_KEY = computed(() => `smerra:journey:${props.productKey ?? 'default'}:info:v6`)

/* --- Préremplissage depuis /me (espace connecté) --------------------------
 * Générique : appliqué à tous les parcours via ce composant partagé. Pour un
 * utilisateur connecté, ces champs affichent EXCLUSIVEMENT la donnée backend
 * (/me) ou restent vides : on ne réhydrate jamais leur valeur depuis le cache
 * sessionStorage (cf. ME_DRIVEN_FIELDS, écartés au montage). Le NIR est prérempli
 * pour l'affichage même s'il n'est pas envoyé pour un souscripteur connecté
 * (identifié par user_id). */
const memberPrefilled = ref(false)

/** Champs de « Vos informations » alimentés par /me. En espace connecté ils ne
 *  sont jamais restaurés depuis le cache (sessionStorage) : la donnée vient du
 *  back ou reste vide. Les autres champs (RIB, paiement, CGU) continuent d'être
 *  mis en cache normalement. */
const ME_DRIVEN_FIELDS = [
  'civility', 'firstName', 'lastName', 'birthDay', 'birthMonth', 'birthYear',
  'country', 'address', 'addressComplement', 'city', 'postcode', 'communeCode',
  'email', 'emailConfirm', 'phoneCountry', 'phone', 'ssn', 'noSsn',
] as const

function applyMemberToInfo() {
  const m = member.value
  if (!m) return
  info.civility = m.civility
  info.firstName = m.firstName
  info.lastName = m.lastName
  info.email = m.email
  info.emailConfirm = m.email
  // Le back stocke le téléphone en E.164 ; on le repasse en pays + national pour
  // alimenter le sélecteur d'indicatif et le champ (le format saisi attendu).
  const parsedPhone = m.phone ? fromE164(m.phone) : null
  if (parsedPhone && (COUNTRY_CODES_PHONE as readonly string[]).includes(parsedPhone.country)) {
    info.phoneCountry = parsedPhone.country as PhoneCountryCode
    info.phone = parsedPhone.national
  }
  const [year, month, day] = (m.birthDate || '').split('-')
  if (year && month && day) {
    info.birthYear = Number(year)
    info.birthMonth = Number(month)
    info.birthDay = Number(day)
  }
  // Champs adresse : seulement s'ils sont renseignés côté /me (sinon on préserve
  // ce que l'utilisateur aurait pu saisir pour compléter une donnée manquante).
  if (m.address.line1) info.address = m.address.line1
  if (m.address.line2) info.addressComplement = m.address.line2
  if (m.address.city) info.city = m.address.city
  if (m.address.postalCode) info.postcode = m.address.postalCode
  if ((COUNTRY_CODES_ADDRESS as readonly string[]).includes(m.address.country)) {
    info.country = m.address.country as AddressCountryCode
  }
  // NIR fourni par /me : on le préremplit et on décoche « pas de NIR »
  // (la case sera aussi désactivée dans le template via fieldLocked(info.ssn)).
  if (m.nir) {
    info.ssn = formatSsn(m.nir)
    info.noSsn = false
  }
  // Restaure la sélection de commune pour une adresse française préremplie
  // (sinon le champ unique ville/CP apparaîtrait vide).
  if (info.country === 'FR' && info.city && info.postcode) {
    commune.restore({
      postalCode: info.postcode,
      city: info.city,
      communeCode: info.communeCode,
      label: `${info.postcode} ${info.city}`,
    })
  }
}

/** Applique /me une fois par montage : connecté et /me chargé (celui-ci peut
 *  répondre après le montage, d'où le watch ci-dessous). Écrase l'état restauré
 *  du sessionStorage pour toujours afficher la donnée backend. */
function maybePrefillFromMember() {
  if (memberPrefilled.value || !isConnected.value || !memberLoaded.value) return
  if (!memberLoaded.value) return
  applyMemberToInfo()
  memberPrefilled.value = true
}
watch(memberLoaded, () => maybePrefillFromMember())

onMounted(() => {
  if (typeof window === 'undefined') return
  // Nettoie les versions antérieures de la clé pour éviter d'accumuler du
  // résiduel dans le sessionStorage de l'utilisateur. L'ancienne clé `:v6` était
  // partagée par tous les parcours (cause de la fuite NIR/RIB inter-parcours) :
  // on la purge aussi.
  try {
    window.sessionStorage.removeItem('smerra:journey:health:info')
    window.sessionStorage.removeItem('smerra:journey:health:info:v2')
    window.sessionStorage.removeItem('smerra:journey:health:info:v3')
    window.sessionStorage.removeItem('smerra:journey:health:info:v4')
    window.sessionStorage.removeItem('smerra:journey:health:info:v5')
    window.sessionStorage.removeItem('smerra:journey:health:info:v6')
  } catch { /* ignore */ }

  let restoredShow = false
  const raw = window.sessionStorage.getItem(SS_INFO_KEY.value)
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        if (parsed.info && typeof parsed.info === 'object') {
          const restored = { ...parsed.info }
          // IBAN/BIC ne sont jamais persistés ; on purge aussi toute valeur
          // résiduelle d'une version antérieure du cache.
          delete restored.iban
          delete restored.bic
          // Espace connecté : ne jamais réafficher les infos perso du cache — elles
          // proviennent de /me (applyMemberToInfo) ou restent vides.
          if (isConnected.value) {
            for (const key of ME_DRIVEN_FIELDS) delete restored[key]
          }
          Object.assign(info, restored)
        }
        if (typeof parsed.step === 'number' && parsed.step >= 1 && parsed.step <= 5) infoStep.value = parsed.step
        if (parsed.show === true) restoredShow = true
      }
    } catch { /* ignore JSON invalide */ }
  }

  // La phase « Vos informations » n'existe qu'une fois l'arbre de décision
  // terminé (currentStepKey === null). On gate le flag restauré sur cet état :
  // une persistance obsolète (parcours précédent) ne doit jamais ré-afficher le
  // formulaire sur un parcours neuf/incomplet. On assigne le model EN UNE FOIS :
  // un set de defineModel n'est pas relisible de façon fiable dans le même tick
  // (la valeur transite par le v-model du parent), donc on calcule d'abord.
  const shouldActivate = restoredShow && currentStepKey.value === null
  active.value = shouldActivate
  if (!shouldActivate) {
    infoStep.value = 1
    infoSubmitted.value = false
  }

  // Restaure la sélection de commune si une ville/CP française a été choisie
  // précédemment — sinon le champ unique apparaîtrait vide alors que
  // info.city/postcode/communeCode sont renseignés.
  if (info.country === 'FR' && info.city && info.postcode) {
    commune.restore({
      postalCode: info.postcode,
      city: info.city,
      communeCode: info.communeCode,
      label: `${info.postcode} ${info.city}`,
    })
  }

  // Préremplissage /me pour un utilisateur connecté sur un parcours neuf.
  maybePrefillFromMember()

  // Si on hydrate dans l'état info-form (tab fraîche, lien direct, retour Stripe
  // → /subscription/summary) et que history.state ne porte pas notre sentinelle,
  // la pile des sous-étapes est perdue : on ancre l'état courant avec une seule
  // entrée. La descente sous-étape par sous-étape est ensuite reconstruite « à la
  // volée » dans onInfoPopState (chaque « Précédent » décrémente + ré-ancre),
  // plutôt que de pré-empiler N entrées synchrones (que le navigateur fusionne).
  if (shouldActivate) {
    const s = history.state as { ji?: string } | null
    if (s?.ji !== INFO_HISTORY_SENTINEL) {
      pushInfoHistory(infoStep.value)
    }
  }
  window.addEventListener('popstate', onInfoPopState)

  watch(
    () => JSON.stringify({ info, step: infoStep.value, show: active.value }),
    () => {
      // IBAN et BIC ne sont JAMAIS persistés (donnée bancaire sensible) : on les
      // exclut de l'objet écrit en sessionStorage.
      const { iban: _iban, bic: _bic, ...persistableInfo } = info
      const json = JSON.stringify({ info: persistableInfo, step: infoStep.value, show: active.value })
      try { window.sessionStorage.setItem(SS_INFO_KEY.value, json) } catch { /* quota */ }
    },
  )
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('popstate', onInfoPopState)
  }
})

// Si l'utilisateur revient dans l'arbre de décision via navigation (back du
// navigateur, changement d'URL direct), on sort de la phase infos.
watch(
  () => currentStepKey.value,
  (key) => {
    if (key !== null && active.value) {
      active.value = false
      infoStep.value = 1
      infoSubmitted.value = false
    }
  },
)

// Every sub-step of the phase (and the final recap) shares the URL of the quote,
// so the router's scrollBehavior never fires for them: reposition at the top on
// each screen change, exactly as a page change would. `flush: 'post'` waits for
// the new screen to be rendered.
watch([active, infoStep], () => scrollToTop(), { flush: 'post' })

/** Ouvre la phase « Vos informations » depuis l'écran récap du parent. Distinct de
 *  l'hydratation sessionStorage (onMounted) qui restaure une sous-étape en cours :
 *  ici on (ré)initialise à la 1re sous-étape et on ancre une entrée d'historique. */
function open() {
  active.value = true
  infoStep.value = 1
  infoSubmitted.value = false
  pushInfoHistory(1)
}
defineExpose({ open })
</script>

<template>
    <div v-if="active" class="journey-info">
      <h1 id="journey-question" class="journey-info__title">
        {{ infoStep <= infoStepCount
          ? t('subscription.info.title', { step: infoStep, total: infoStepCount })
          : t('subscription.info.finalRecap.title') }}
      </h1>

      <form
        v-if="infoStep === 1"
        class="journey-info__form"
        novalidate
        @submit.prevent="submitInfoStep"
      >
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>
        <div class="journey-form__field" role="radiogroup" :aria-label="t('subscription.info.civility.label')">
          <span class="journey-form__label" id="info-civility-label">
            {{ t('subscription.info.civility.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </span>
          <div
            class="journey-form__radios"
            :class="{ 'journey-form__radios--invalid': infoErrors.civility }"
          >
            <label
              v-for="opt in (['mr', 'mrs'] as const)"
              :key="opt"
              class="journey-form__radio"
              :class="{ 'journey-form__radio--checked': info.civility === opt }"
            >
              <input
                type="radio"
                name="info-civility"
                class="journey-form__radio-input"
                :value="opt"
                v-model="info.civility"
                :disabled="fieldLocked(info.civility)"
              />
              <span class="journey-form__radio-text">
                {{ t(`subscription.info.civility.options.${opt}`) }}
              </span>
            </label>
          </div>
          <p v-if="infoErrors.civility" class="journey-form__error">{{ infoErrors.civility }}</p>
        </div>

        <div class="journey-form__field">
          <label class="journey-form__label" for="info-lastname">
            {{ t('subscription.info.lastName.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <input
            id="info-lastname"
            v-model="info.lastName"
            :disabled="fieldLocked(info.lastName)"
            type="text"
            class="journey-form__input"
            :class="{ 'journey-form__input--invalid': infoErrors.lastName }"
            :placeholder="t('subscription.info.lastName.placeholder')"
            autocomplete="family-name"
          />
          <p v-if="infoErrors.lastName" class="journey-form__error">{{ infoErrors.lastName }}</p>
        </div>

        <div class="journey-form__field">
          <label class="journey-form__label" for="info-firstname">
            {{ t('subscription.info.firstName.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <input
            id="info-firstname"
            v-model="info.firstName"
            :disabled="fieldLocked(info.firstName)"
            type="text"
            class="journey-form__input"
            :class="{ 'journey-form__input--invalid': infoErrors.firstName }"
            :placeholder="t('subscription.info.firstName.placeholder')"
            autocomplete="given-name"
          />
          <p v-if="infoErrors.firstName" class="journey-form__error">{{ infoErrors.firstName }}</p>
        </div>

        <fieldset class="journey-form__field journey-info__date-field">
          <legend class="journey-form__label">
            {{ t('subscription.info.birthDate.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </legend>
          <div
            class="journey-info__date-row"
            :class="{ 'journey-info__date-row--invalid': infoErrors.birthDate }"
          >
            <div class="journey-form__select">
              <select
                v-model.number="info.birthDay"
                :disabled="fieldLocked(info.birthYear)"
                class="journey-form__select-el"
                :aria-label="t('subscription.info.birthDate.day')"
              >
                <option :value="null" disabled>{{ t('subscription.info.birthDate.day') }}</option>
                <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
              </select>
              <svg class="journey-form__select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="journey-form__select">
              <select
                v-model.number="info.birthMonth"
                :disabled="fieldLocked(info.birthYear)"
                class="journey-form__select-el"
                :aria-label="t('subscription.info.birthDate.month')"
              >
                <option :value="null" disabled>{{ t('subscription.info.birthDate.month') }}</option>
                <option v-for="m in monthsList" :key="m.value" :value="m.value">{{ m.label }}</option>
              </select>
              <svg class="journey-form__select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="journey-form__select">
              <select
                v-model.number="info.birthYear"
                :disabled="fieldLocked(info.birthYear)"
                class="journey-form__select-el"
                :aria-label="t('subscription.info.birthDate.year')"
              >
                <option :value="null" disabled>{{ t('subscription.info.birthDate.year') }}</option>
                <option v-for="y in birthYearOptions" :key="y" :value="y">{{ y }}</option>
              </select>
              <svg class="journey-form__select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
          <p v-if="infoErrors.birthDate" class="journey-form__error">{{ infoErrors.birthDate }}</p>
        </fieldset>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.info.submit') }}
        </button>
      </form>

      <form
        v-else-if="infoStep === 2"
        class="journey-info__form"
        novalidate
        @submit.prevent="submitInfoStep"
      >
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>

        <div class="journey-form__field">
          <label class="journey-form__label" for="info-country">
            {{ t('subscription.info.country.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <div
            class="journey-form__select"
            :class="{ 'journey-form__select--invalid': infoErrors.country }"
          >
            <select
              id="info-country"
              v-model="info.country"
              :disabled="fieldLocked(info.address)"
              class="journey-form__select-el"
              @change="onCountryChange"
            >
              <option
                v-for="opt in countryOptions"
                :key="opt.code"
                :value="opt.code"
              >{{ opt.label }}</option>
            </select>
            <svg class="journey-form__select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
              <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <p v-if="infoErrors.country" class="journey-form__error">{{ infoErrors.country }}</p>
        </div>

        <div class="journey-form__field">
          <label class="journey-form__label" for="info-address">
            {{ t('subscription.info.address.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <input
            id="info-address"
            v-model="info.address"
            :disabled="fieldLocked(info.address)"
            type="text"
            class="journey-form__input"
            :class="{ 'journey-form__input--invalid': infoErrors.address }"
            :placeholder="t('subscription.info.address.placeholder')"
            autocomplete="street-address"
          />
          <p v-if="infoErrors.address" class="journey-form__error">{{ infoErrors.address }}</p>
        </div>

        <div class="journey-form__field">
          <label class="journey-form__label" for="info-address-complement">
            {{ t('subscription.info.addressComplement.label') }}
            <span class="journey-form__optional">({{ t('subscription.info.addressComplement.optional') }})</span>
          </label>
          <input
            id="info-address-complement"
            v-model="info.addressComplement"
            :disabled="fieldLocked(info.addressComplement)"
            type="text"
            class="journey-form__input"
            :placeholder="t('subscription.info.addressComplement.placeholder')"
            autocomplete="address-line2"
          />
        </div>

        <!-- Ville/CP : autocomplete commune (geo.api.gouv.fr) si France, sinon
             2 inputs libres. Un seul champ : code postal partiel ou nom de ville. -->
        <div v-if="isCountryFrance" class="journey-form__field journey-info__autocomplete">
          <label class="journey-form__label" for="info-city-postcode">
            {{ t('subscription.info.cityPostcode.labelFr') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <input
            id="info-city-postcode"
            v-model="cityPostcodeModel"
            :disabled="fieldLocked(info.city) && fieldLocked(info.postcode)"
            type="text"
            class="journey-form__input"
            :class="{ 'journey-form__input--invalid': infoErrors.cityPostcode || cityPostcodeAsyncError }"
            :placeholder="t('subscription.info.cityPostcode.placeholderFr')"
            :name="cityFieldName"
            autocomplete="new-password"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="commune.open.value && (commune.suggestions.value.length > 0 || commune.loading.value)"
            aria-controls="info-city-postcode-list"
            @focus="commune.onFocus()"
            @blur="commune.onBlur()"
          />
          <ul
            v-if="commune.open.value && commune.query.value.length >= 1"
            id="info-city-postcode-list"
            class="journey-info__autocomplete-list"
            role="listbox"
          >
            <li
              v-if="commune.belowThreshold.value"
              class="journey-info__autocomplete-item journey-info__autocomplete-item--muted"
              aria-disabled="true"
            >
              {{ t('subscription.info.cityPostcode.minChars') }}
            </li>
            <li
              v-else-if="commune.loading.value"
              class="journey-info__autocomplete-item journey-info__autocomplete-item--muted"
              aria-disabled="true"
            >
              {{ t('subscription.info.cityPostcode.loading') }}
            </li>
            <li
              v-else-if="commune.suggestions.value.length === 0"
              class="journey-info__autocomplete-item journey-info__autocomplete-item--muted"
              aria-disabled="true"
            >
              {{ t('subscription.info.cityPostcode.empty') }}
            </li>
            <template v-else>
              <li
                v-for="s in commune.suggestions.value"
                :key="s.key"
                class="journey-info__autocomplete-item"
                role="option"
                :aria-selected="commune.selected.value?.communeCode === s.communeCode && commune.selected.value?.postalCode === s.postalCode"
                @mousedown.prevent="pickCommune(s)"
              >
                <span class="journey-info__autocomplete-label">{{ s.label }}</span>
              </li>
            </template>
          </ul>
          <p v-if="infoErrors.cityPostcode" class="journey-form__error">{{ infoErrors.cityPostcode }}</p>
          <p v-else-if="cityPostcodeAsyncError" class="journey-form__error">{{ cityPostcodeAsyncError }}</p>
        </div>

        <div v-else class="journey-form__field">
          <span class="journey-form__label">
            {{ t('subscription.info.cityPostcode.labelOther') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </span>
          <div class="journey-info__city-row">
            <input
              v-model="info.city"
              :disabled="fieldLocked(info.city)"
              type="text"
              class="journey-form__input"
              :class="{ 'journey-form__input--invalid': infoErrors.city }"
              :placeholder="t('subscription.info.cityPostcode.cityPlaceholder')"
              autocomplete="address-level2"
              :aria-label="t('subscription.info.cityPostcode.cityPlaceholder')"
            />
            <input
              v-model="info.postcode"
              :disabled="fieldLocked(info.postcode)"
              type="text"
              class="journey-form__input journey-info__postcode"
              :class="{ 'journey-form__input--invalid': infoErrors.postcode }"
              :placeholder="t('subscription.info.cityPostcode.postcodePlaceholder')"
              autocomplete="postal-code"
              :aria-label="t('subscription.info.cityPostcode.postcodePlaceholder')"
            />
          </div>
          <p v-if="infoErrors.city" class="journey-form__error">{{ infoErrors.city }}</p>
          <p v-if="infoErrors.postcode" class="journey-form__error">{{ infoErrors.postcode }}</p>
        </div>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.info.submit') }}
        </button>
      </form>

      <form
        v-else-if="infoStep === 3"
        class="journey-info__form"
        novalidate
        @submit.prevent="submitInfoStep"
      >
        <p class="journey-form__required-legend">
          <span class="journey-form__required" aria-hidden="true">*</span>
          {{ t('common.requiredLegend') }}
        </p>

        <div class="journey-form__field">
          <label class="journey-form__label" for="info-email">
            {{ t('subscription.info.email.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <input
            id="info-email"
            v-model="info.email"
            :disabled="fieldLocked(info.email)"
            type="email"
            class="journey-form__input"
            :class="{ 'journey-form__input--invalid': infoErrors.email }"
            :placeholder="t('subscription.info.email.placeholder')"
            autocomplete="email"
            inputmode="email"
          />
          <p v-if="infoErrors.email" class="journey-form__error">{{ infoErrors.email }}</p>
        </div>

        <div v-if="!isConnected" class="journey-form__field">
          <label class="journey-form__label" for="info-email-confirm">
            {{ t('subscription.info.emailConfirm.label') }}
            <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
          </label>
          <input
            id="info-email-confirm"
            v-model="info.emailConfirm"
            :disabled="fieldLocked(info.email)"
            type="email"
            class="journey-form__input"
            :class="{ 'journey-form__input--invalid': infoErrors.emailConfirm }"
            :placeholder="t('subscription.info.emailConfirm.placeholder')"
            autocomplete="email"
            inputmode="email"
            onpaste="return false"
          />
          <p v-if="infoErrors.emailConfirm" class="journey-form__error">{{ infoErrors.emailConfirm }}</p>
        </div>

        <div class="journey-form__field">
          <label class="journey-form__label" for="info-phone">
            {{ t('subscription.info.phone.label') }}
            <span class="journey-form__optional">({{ t('subscription.info.phone.optional') }})</span>
          </label>
          <div
            class="journey-info__phone-row"
            :class="{ 'journey-info__phone-row--invalid': infoErrors.phoneInvalid }"
          >
            <div class="journey-form__select journey-info__phone-dial">
              <select
                v-model="info.phoneCountry"
                :disabled="fieldLocked(info.phone)"
                class="journey-form__select-el journey-info__phone-dial-el"
                :aria-label="t('subscription.info.phone.dialLabel')"
                :title="phoneCountryTitle"
              >
                <option
                  v-for="opt in phoneCountryOptions"
                  :key="opt.code"
                  :value="opt.code"
                  :title="`${opt.name} (${opt.dial})`"
                >{{ opt.flag }} {{ opt.dial }}</option>
              </select>
              <svg class="journey-form__select-chevron" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
                <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <input
              id="info-phone"
              :value="info.phone"
              @input="onPhoneInput"
              :disabled="fieldLocked(info.phone)"
              type="tel"
              class="journey-form__input journey-info__phone-input"
              :placeholder="t('subscription.info.phone.placeholder')"
              autocomplete="tel-national"
              inputmode="tel"
            />
          </div>
          <p v-if="infoErrors.phoneInvalid" class="journey-form__error">{{ infoErrors.phoneInvalid }}</p>
        </div>

        <div v-if="assiaForwardingAutomatic" class="journey-form__field">
          <label class="journey-form__label" for="info-ssn">
            {{ t('subscription.info.ssn.label') }}
            <span class="journey-form__optional">({{ t('subscription.info.ssn.optional') }})</span>
          </label>
          <input
            id="info-ssn"
            :value="info.ssn"
            @input="onSsnInput"
            type="text"
            class="journey-form__input"
            :class="{ 'journey-form__input--invalid': infoErrors.ssn }"
            :placeholder="t('subscription.info.ssn.placeholder')"
            :disabled="info.noSsn || fieldLocked(info.ssn)"
            autocomplete="off"
            inputmode="numeric"
            maxlength="21"
          />
          <label class="journey-info__checkbox">
            <input
              type="checkbox"
              v-model="info.noSsn"
              @change="onNoSsnToggle"
              :disabled="fieldLocked(info.ssn)"
              class="journey-info__checkbox-input"
            />
            <span class="journey-info__checkbox-label">{{ t('subscription.info.ssn.noSsn') }}</span>
          </label>
          <p v-if="infoErrors.ssn" class="journey-form__error">{{ infoErrors.ssn }}</p>
        </div>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.info.submit') }}
        </button>
      </form>

      <form
        v-else-if="infoStep === 4 && assiaForwardingAutomatic"
        class="journey-info__form"
        novalidate
        @submit.prevent="submitInfoStep"
      >
        <div class="journey-info__bank-intro">
          <p class="journey-info__bank-intro-title">{{ t('subscription.info.iban.intro') }}</p>
          <p class="journey-info__bank-intro-sepa">{{ t('subscription.info.iban.sepa') }}</p>
        </div>

        <!-- Choix « maintenant / plus tard » : « plus tard » masque le RIB,
             que l'étudiant renseignera ensuite depuis son espace personnel
             (la validation infoErrors est court-circuitée quand ibanLater). -->
        <fieldset class="journey-info__rib-mode">
          <legend class="journey-info__rib-mode-legend">{{ t('subscription.info.iban.modeLegend') }}</legend>
          <label
            class="journey-info__rib-option"
            :class="{ 'is-selected': !info.ibanLater }"
          >
            <!-- Same grouping as the payment modality above, and for the same reason. -->
            <input
              type="radio"
              name="rib-mode"
              v-model="info.ibanLater"
              :value="false"
              class="journey-info__rib-radio"
            />
            <span>{{ t('subscription.info.iban.modeNow') }}</span>
          </label>
          <label
            class="journey-info__rib-option"
            :class="{ 'is-selected': info.ibanLater }"
          >
            <input
              type="radio"
              name="rib-mode"
              v-model="info.ibanLater"
              :value="true"
              class="journey-info__rib-radio"
            />
            <span>{{ t('subscription.info.iban.later') }}</span>
          </label>
        </fieldset>

        <!-- Champs RIB affichés uniquement en mode « je renseigne maintenant ». -->
        <template v-if="!info.ibanLater">
          <p class="journey-form__required-legend">
            <span class="journey-form__required" aria-hidden="true">*</span>
            {{ t('common.requiredLegend') }}
          </p>

          <div class="journey-form__field">
            <label class="journey-form__label" for="info-iban">
              {{ t('subscription.info.iban.ibanLabel') }}
              <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
            </label>
            <input
              id="info-iban"
              v-model="info.iban"
              type="text"
              class="journey-form__input journey-info__bank-input"
              :class="{ 'journey-form__input--invalid': infoErrors.iban }"
              autocomplete="off"
              inputmode="text"
              spellcheck="false"
            />
            <p v-if="infoErrors.iban" class="journey-form__error">{{ infoErrors.iban }}</p>
            <p v-else class="journey-form__hint journey-info__bank-example">
              {{ t('subscription.info.iban.ibanExample') }}
            </p>
          </div>

          <div class="journey-form__field">
            <label class="journey-form__label" for="info-bic">
              {{ t('subscription.info.iban.bicLabel') }}
              <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
            </label>
            <input
              id="info-bic"
              v-model="info.bic"
              type="text"
              class="journey-form__input journey-info__bank-input"
              :class="{ 'journey-form__input--invalid': infoErrors.bic }"
              autocomplete="off"
              inputmode="text"
              spellcheck="false"
            />
            <p v-if="infoErrors.bic" class="journey-form__error">{{ infoErrors.bic }}</p>
            <p v-else class="journey-form__hint journey-info__bank-example">
              {{ t('subscription.info.iban.bicExample') }}
            </p>
          </div>

          <div class="journey-form__field">
            <label class="journey-form__label" for="info-iban-holder">
              {{ t('subscription.info.iban.holderLabel') }}
              <span class="journey-form__required" :aria-label="t('common.requiredMark')">*</span>
            </label>
            <input
              id="info-iban-holder"
              v-model="info.ibanHolder"
              type="text"
              class="journey-form__input"
              :class="{ 'journey-form__input--invalid': infoErrors.ibanHolder }"
              :placeholder="t('subscription.info.iban.holderPlaceholder')"
              autocomplete="name"
            />
            <p v-if="infoErrors.ibanHolder" class="journey-form__error">{{ infoErrors.ibanHolder }}</p>
          </div>
        </template>

        <button type="submit" class="journey-form__submit">
          {{ t('subscription.info.submit') }}
        </button>
      </form>

      <!-- Récap final générique (produits branchés au back-end : santé, étranger,
           « en France ») : cartes + dates + total + paiement + CGV + Club + Payer,
           le tout piloté par recapView ; les specifics (sélecteur de paiement,
           tacite, mode de prix, Club) sont déclarés dans la stratégie du flow. -->
      <form
        v-else-if="infoStep === infoRecapStep && recapView"
        class="journey-info__form journey-info__final"
        novalidate
        @submit.prevent="submitPayment"
      >
        <article
          v-for="(card, i) in recapView.cards"
          :key="i"
          class="recap-card"
          :class="{ 'recap-card--secondary': i > 0 }"
        >
          <div class="recap-card__media">
            <img :src="card.image" :alt="card.name" width="800" height="446" loading="lazy" />
          </div>
          <div class="recap-card__body">
            <p class="recap-card__category">{{ card.category }}</p>
            <h2 class="recap-card__name">{{ card.name }}</h2>
            <dl v-if="recapView.dateRows.length" class="recap-card__dates">
              <div
                v-for="row in recapView.dateRows"
                :key="row.label"
                class="recap-card__date-row"
              >
                <dt>{{ row.label }}</dt>
                <dd>{{ row.value }}</dd>
              </div>
            </dl>
            <p v-if="cardPriceText(card)" class="recap-card__price">
              <span class="recap-card__price-strong">{{ cardPriceText(card) }}</span>
            </p>
          </div>
        </article>

        <p v-if="recapTotalText" class="recap-total">
          <span class="recap-total__label">{{ t('subscription.info.finalRecap.total') }}</span>
          <span class="recap-total__value">{{ recapTotalText }}</span>
        </p>

        <!-- Modalités de paiement : masquées quand seul l'upfront est possible
             (mensuel offert uniquement à partir de 2 mois de couverture). -->
        <fieldset v-if="recapView.showPaymentSelector && recapView.monthlyAvailable" class="recap-payment">
          <legend class="recap-payment__legend">{{ t('subscription.info.finalRecap.payment.label') }}</legend>
          <!-- `name` is what makes these two a radio group for the browser, and without it they
               were only a group as far as v-model was concerned. Coming back from Stripe, the
               browser restores each form control from its own history record; ungrouped, nothing
               unchecks the other, and both dots end up filled while the highlighted border still
               follows the single value Vue holds. The dot is the native control (see
               accent-color below), so only the browser can be made to arbitrate it. -->
          <label class="recap-payment__option" :class="{ 'is-selected': info.paymentMode === 'upfront' }">
            <input type="radio" name="recap-payment-mode" v-model="info.paymentMode" value="upfront" class="recap-payment__radio" />
            <span>{{ t('subscription.info.finalRecap.payment.upfront') }}</span>
          </label>
          <label class="recap-payment__option" :class="{ 'is-selected': info.paymentMode === 'monthly' }">
            <input type="radio" name="recap-payment-mode" v-model="info.paymentMode" value="monthly" class="recap-payment__radio" />
            <span>{{ t('subscription.info.finalRecap.payment.monthly') }}</span>
          </label>
        </fieldset>

        <!-- Commercial prospecting opt-in (optional, independent of the terms) -->
        <label class="journey-info__checkbox recap-prospecting">
          <input type="checkbox" v-model="info.prospectingOptIn" class="journey-info__checkbox-input" />
          <span class="journey-info__checkbox-label">{{ t('subscription.info.finalRecap.prospecting.checkbox') }}</span>
        </label>

        <!-- CGV : checkbox + bloc dépliable -->
        <div class="recap-terms">
          <label class="journey-info__checkbox recap-terms__checkbox">
            <input type="checkbox" v-model="info.termsAccepted" class="journey-info__checkbox-input" />
            <span class="journey-info__checkbox-label">{{ t('subscription.info.finalRecap.terms.checkbox') }}</span>
          </label>
          <button
            type="button"
            class="recap-terms__toggle"
            :aria-expanded="info.termsExpanded"
            aria-controls="recap-terms-expanded-final"
            @click="info.termsExpanded = !info.termsExpanded"
          >
            {{ info.termsExpanded
              ? t('subscription.info.finalRecap.terms.readLess')
              : t('subscription.info.finalRecap.terms.readMore') }}
            <svg
              class="recap-terms__chevron"
              :class="{ 'is-open': info.termsExpanded }"
              viewBox="0 0 20 20" width="14" height="14" aria-hidden="true"
            >
              <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div v-if="info.termsExpanded" id="recap-terms-expanded-final" class="recap-terms__expanded">
            <p>{{ t('subscription.info.finalRecap.terms.tacitParagraph') }}</p>
            <p>
              <strong>{{ t('subscription.info.finalRecap.terms.articleTitle') }}</strong><br />
              {{ t('subscription.info.finalRecap.terms.articleBody') }}
            </p>
            <p>{{ t('subscription.info.finalRecap.terms.uitsemParagraph') }}</p>
            <p class="recap-terms__links">
              <a href="/documents/formulaire-retractation-smerra.pdf" target="_blank" rel="noopener">{{ t('subscription.info.finalRecap.terms.retractationLink') }}</a>
              <a href="https://smerra.fr/mentions-legales/" target="_blank" rel="noopener">{{ t('subscription.info.finalRecap.terms.statutsLink') }}</a>
            </p>
          </div>
        </div>

        <!-- Encart Club STUD'LIFE (réassurance), uniquement si la stratégie l'active. -->
        <aside v-if="recapView.showClub" class="recap-club">
          <div class="recap-club__media">
            <img :src="recapView.clubImage" :alt="t('subscription.info.finalRecap.club.imageAlt')" width="600" height="400" loading="lazy" />
          </div>
          <div class="recap-club__body">
            <span class="recap-club__badge">{{ t('subscription.info.finalRecap.club.badge') }}</span>
            <h3 class="recap-club__title">{{ t('subscription.info.finalRecap.club.title') }}</h3>
            <p class="recap-club__intro">{{ t('subscription.info.finalRecap.club.intro') }}</p>
            <p class="recap-club__benefits">{{ t('subscription.info.finalRecap.club.benefits') }}</p>
          </div>
        </aside>

        <div class="recap-actions">
          <button
            type="button"
            class="journey-form__submit recap-share"
            :disabled="!info.termsAccepted || finalizing"
            @click="sharePaymentLink"
          >
            <span class="recap-share__main">{{ t('subscription.info.finalRecap.shareLink') }}</span>
            <span class="recap-share__sub">{{ t('subscription.info.finalRecap.shareLinkSub') }}</span>
          </button>
          <span class="recap-actions__or" aria-hidden="true">{{ t('subscription.info.finalRecap.or') }}</span>
          <button
            type="submit"
            class="journey-form__submit recap-pay"
            :disabled="!info.termsAccepted || finalizing"
          >
            {{ redirecting ? t('subscription.info.payment.redirecting') : t('subscription.info.finalRecap.pay') }}
          </button>
        </div>
        <p v-if="!info.termsAccepted" class="journey-form__hint recap-pay__hint">
          {{ t('subscription.info.finalRecap.termsRequired') }}
        </p>
        <!-- Email déjà rattaché à un compte : message dédié + lien vers la connexion. -->
        <i18n-t
          v-else-if="finalizeError === 'subscription.info.payment.emailTaken'"
          keypath="subscription.info.payment.emailTaken"
          tag="p"
          class="journey-form__hint recap-pay__error"
          role="alert"
          scope="global"
        >
          <template #login>
            <router-link :to="loginHref" class="recap-pay__error-link">
              {{ t('subscription.info.payment.emailTakenLink') }}
            </router-link>
          </template>
        </i18n-t>
        <p v-else-if="finalizeError" class="journey-form__hint recap-pay__error" role="alert">
          {{ t(finalizeError) }}
        </p>
      </form>

      <div v-else class="journey-info__placeholder">
        <p>{{ t('subscription.info.stepPlaceholder', { step: infoStep }) }}</p>
      </div>
    </div>
</template>

<style scoped>
/* ---- Formulaire de souscription ---- */
.journey-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.journey-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
/* Deux date pickers côte à côte (séjour étranger : début / fin). */
.journey-form__duo {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: 1fr;
}
@media (min-width: 26rem) {
  .journey-form__duo { grid-template-columns: 1fr 1fr; }
}
.journey-form__subfield {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
}
.journey-form__label {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
}
.journey-form__required {
  color: var(--danger);
  font-weight: 700;
  margin-left: 0.15em;
}
.journey-form__required-legend {
  display: flex;
  align-items: baseline;
  gap: 0.35em;
  font-size: var(--text-xs);
  color: var(--text-muted);
}
.journey-form__required-legend .journey-form__required {
  margin-left: 0;
}
.journey-form__error {
  font-size: var(--text-xs);
  font-weight: 600;
  color: var(--danger);
}
.journey-form__hint {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--primary);
}

/* Select stylé à la charte (chevron custom). */
.journey-form__select {
  position: relative;
  display: flex;
  align-items: center;
}
.journey-form__select-el {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  padding: 0.7rem 2.5rem 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.journey-form__select-el:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.journey-form__select--invalid .journey-form__select-el {
  border-color: var(--danger);
}
.journey-form__select-chevron {
  position: absolute;
  right: 0.9rem;
  color: var(--text-muted);
  pointer-events: none;
}

/* Durée : boutons radio segmentés (UX plus directe qu'un select). */
.journey-form__radios {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}
.journey-form__radio {
  flex: 1 1 0;
  min-width: 6rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.6rem;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out),
    box-shadow var(--dur-fast) var(--ease-out),
    background var(--dur-fast) var(--ease-out);
}
.journey-form__radio:hover {
  border-color: var(--primary);
}
.journey-form__radio--checked {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--primary) 10%, white);
  color: var(--primary);
  font-weight: 700;
}
/* Pastille radio visible (cercle + point au centre quand coché). */
.journey-form__radio-input {
  appearance: none;
  -webkit-appearance: none;
  flex: 0 0 auto;
  width: 1.15rem;
  height: 1.15rem;
  margin: 0;
  border: 2px solid var(--border-strong);
  border-radius: 50%;
  background: var(--color-white);
  cursor: pointer;
  display: grid;
  place-content: center;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.journey-form__radio-input::before {
  content: '';
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--primary);
  transform: scale(0);
  transition: transform var(--dur-fast) var(--ease-out);
}
.journey-form__radio-input:checked {
  border-color: var(--primary);
}
.journey-form__radio-input:checked::before {
  transform: scale(1);
}
.journey-form__radio-input:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
.journey-form__radios--invalid .journey-form__radio {
  border-color: var(--danger);
}
.journey-form__radios--invalid .journey-form__radio-input {
  border-color: var(--danger);
}

.journey-form__notice {
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--text-muted);
  padding: 0.75rem 0.9rem;
  background: var(--bg-soft);
  border-radius: var(--radius-md);
}

.journey-form__submit {
  align-self: flex-start;
  padding: 0.8rem 2rem;
  background: var(--primary);
  border: 1px solid var(--primary);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-on-primary);
  cursor: pointer;
  transition:
    background var(--dur-fast) var(--ease-out),
    border-color var(--dur-fast) var(--ease-out),
    transform var(--dur-base) var(--ease-spring);
}
.journey-form__submit:hover {
  background: var(--primary-hover);
  border-color: var(--primary-hover);
  transform: translateY(-1px);
}
.journey-form__submit:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}
@media (max-width: 30rem) {
  .journey-form__submit { align-self: stretch; text-align: center; }
}

/* Input texte stylé à la charte (cohérent avec .journey-form__select-el). */
.journey-form__input {
  width: 100%;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.journey-form__input::placeholder { color: var(--text-muted); }
.journey-form__input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.journey-form__input--invalid { border-color: var(--danger); }

/* Champ prérempli depuis /me et verrouillé (utilisateur connecté) : lecture
   seule, valeur bien lisible mais visiblement non éditable. */
.journey-form__input:disabled,
.journey-form__select-el:disabled {
  background: var(--bg-soft);
  cursor: not-allowed;
}
.journey-form__radio-input:disabled { cursor: not-allowed; }
.journey-form__radio-input:disabled + .journey-form__radio-text { opacity: 0.6; }

/* ---- Phase « Vos informations » (4 sous-étapes) ---- */
.journey-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.journey-info__title {
  font-size: var(--text-xl);
  line-height: var(--leading-tight);
}
.journey-info__form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

/* Fieldset n'a pas de styles par défaut utilisables : reset. */
.journey-info__date-field {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}

/* Trio de selects jour / mois / année. Mobile : ratio 1.1 / 1.6 / 1.3
   pour donner plus de place au mois (libellé long). Petit écran : empilés. */
.journey-info__date-row {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1.1fr 1.6fr 1.3fr;
}
@media (max-width: 26rem) {
  .journey-info__date-row { grid-template-columns: 1fr; }
}
.journey-info__date-row--invalid .journey-form__select-el {
  border-color: var(--danger);
}

.journey-info__placeholder {
  padding: 1rem;
  background: var(--bg-soft);
  border-radius: var(--radius-md);
  color: var(--text-muted);
  font-style: italic;
}

/* ---- Step 2 : adresse + autocomplete ville/CP ---- */
.journey-form__optional {
  margin-left: 0.3em;
  font-weight: 500;
  font-size: var(--text-xs);
  color: var(--text-muted);
  text-transform: lowercase;
}
.journey-info__city-row {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.55fr);
}
@media (max-width: 26rem) {
  .journey-info__city-row { grid-template-columns: 1fr; }
}
.journey-info__autocomplete {
  position: relative;
}
.journey-info__autocomplete-list {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  z-index: 5;
  max-height: 16rem;
  overflow-y: auto;
  margin: 0;
  padding: 0.25rem 0;
  list-style: none;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-md);
}
.journey-info__autocomplete-item {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.55rem 0.85rem;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out);
}
.journey-info__autocomplete-item:hover,
.journey-info__autocomplete-item[aria-selected="true"] {
  background: color-mix(in oklab, var(--color-peach) 18%, white);
}
.journey-info__autocomplete-item--muted {
  color: var(--text-muted);
  font-style: italic;
  cursor: default;
}
.journey-info__autocomplete-item--muted:hover {
  background: transparent;
}
.journey-info__autocomplete-label {
  font-weight: 600;
}
.journey-info__autocomplete-context {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

/* ---- Step 3 : email + téléphone + SSN ---- */
/* Téléphone : indicatif (select drapeau + code) + numéro, en ligne. Le select
   affiche « 🇫🇷 +33 » (compact) ; le nom complet du pays apparaît dans le
   tooltip (title) et dans le menu déroulant natif via attribut title sur
   chaque option. */
.journey-info__phone-row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
}
.journey-info__phone-dial {
  position: relative;
  flex: 0 0 7.75rem;
  min-width: 0;
}
.journey-info__phone-dial-el {
  width: 100%;
  /* Renforce le padding-droite (chevron) pour ne pas chevaucher
     « 🇫🇷 +XX » dans le rendu fermé du select. */
  padding-right: 2.75rem;
}
.journey-info__phone-input {
  flex: 1;
  min-width: 0;
}
.journey-info__phone-row--invalid .journey-info__phone-input,
.journey-info__phone-row--invalid .journey-info__phone-dial-el {
  border-color: var(--danger);
}

.journey-info__checkbox {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.4rem;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  cursor: pointer;
  user-select: none;
}
.journey-info__checkbox-input {
  width: 1.05rem;
  height: 1.05rem;
  margin: 0;
  accent-color: var(--primary);
  cursor: pointer;
  flex-shrink: 0;
}
.journey-info__checkbox-label {
  line-height: 1.3;
}
/* Choix « maintenant / plus tard » en tête de l'étape RIB : 2 radios mis en
   avant pour que l'utilisateur comprenne d'emblée qu'il peut différer la saisie
   (sélectionner « plus tard » masque les champs). Même registre visuel que les
   radios de modalité de paiement (recap-payment). */
.journey-info__rib-mode {
  border: 0;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.journey-info__rib-mode-legend {
  padding: 0;
  margin-bottom: 0.5rem;
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
}
.journey-info__rib-option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
}
.journey-info__rib-option:hover { border-color: var(--primary); }
.journey-info__rib-option.is-selected {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--color-peach) 14%, white);
  box-shadow: 0 0 0 1px var(--primary);
}
.journey-info__rib-radio {
  accent-color: var(--primary);
  cursor: pointer;
  flex-shrink: 0;
}

/* ---- Step 4 : IBAN/BIC/titulaire ---- */
.journey-info__bank-intro {
  padding: 0.85rem 1rem;
  background: color-mix(in oklab, var(--color-peach) 14%, white);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.journey-info__bank-intro-title {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
}
.journey-info__bank-intro-sepa {
  font-size: var(--text-xs);
  color: var(--text-muted);
}
/* Affichage en majuscules pour IBAN/BIC sans modifier la valeur saisie ;
   le validateur normalise (uppercase + sans espaces) côté logique. */
.journey-info__bank-input {
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
}
.journey-info__bank-input::placeholder {
  text-transform: none;
  letter-spacing: normal;
}
/* Exemple sous les champs IBAN/BIC : ton discret (gris) plutôt que le primaire
   du hint par défaut, pour ne pas le confondre avec une valeur active. */
.journey-info__bank-example {
  color: var(--text-muted);
  font-weight: 500;
}

/* ---- Step 5 : récap final ---- */
.journey-info__final {
  gap: 1.25rem;
}

/* Carte produit principale + carte secondaire (Tonic-Études) */
.recap-card {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;
  padding: 1rem;
  background: color-mix(in oklab, var(--color-peach) 10%, white);
  border: 1px solid var(--primary);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}
@media (min-width: 30rem) {
  .recap-card { grid-template-columns: minmax(0, 11rem) minmax(0, 1fr); align-items: center; }
}
.recap-card--secondary {
  background: var(--bg-soft);
  border-color: var(--border-strong);
  box-shadow: none;
}
.recap-card__media {
  overflow: hidden;
  border-radius: var(--radius-md);
  background: color-mix(in oklab, var(--color-peach) 16%, white);
}
.recap-card__media img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 800 / 446;
  object-fit: cover;
}
.recap-card__body {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  min-width: 0;
}
.recap-card__category {
  font-size: var(--text-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
}
.recap-card__name {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--color-chocolate);
}
.recap-card__dates {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  margin: 0.15rem 0 0;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
}
.recap-card__date-row {
  display: flex;
  gap: 0.4rem;
  align-items: baseline;
}
.recap-card__date-row dt {
  font-weight: 600;
  color: var(--text-muted);
}
.recap-card__date-row dt::after { content: ':'; }
.recap-card__date-row dd {
  margin: 0;
  font-weight: 600;
}
.recap-card__price {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-top: 0.4rem;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
}
.recap-card__price-strong {
  font-size: var(--text-lg);
  font-weight: 800;
}
.recap-card__price-alt {
  font-size: var(--text-xs);
  color: var(--text-muted);
}
.recap-card__price--small {
  margin-top: 0.2rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-muted);
}

/* Ligne TOTAL */
.recap-total {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.1rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  flex-wrap: wrap;
}
.recap-total__label {
  font-size: var(--text-sm);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-chocolate);
}
.recap-total__value {
  font-size: var(--text-lg);
  font-weight: 800;
  color: var(--primary);
}

/* Radios modalité de paiement */
.recap-payment {
  border: 0;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.recap-payment__legend {
  padding: 0;
  margin-bottom: 0.5rem;
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
}
.recap-payment__option {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 0.9rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out);
}
.recap-payment__option:hover { border-color: var(--primary); }
.recap-payment__option.is-selected {
  border-color: var(--primary);
  background: color-mix(in oklab, var(--color-peach) 14%, white);
  box-shadow: 0 0 0 1px var(--primary);
}
.recap-payment__radio {
  accent-color: var(--primary);
  cursor: pointer;
  flex-shrink: 0;
}

/* Prospecting opt-in: same register as the terms checkbox, multi-line label
   aligned on the top of the box. */
.recap-prospecting {
  margin-top: 0;
  align-items: flex-start;
}
.recap-prospecting .journey-info__checkbox-input {
  margin-top: 0.15rem;
}

/* CGV : checkbox + bloc dépliable */
.recap-terms {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.recap-terms__checkbox {
  margin-top: 0;
  align-items: flex-start;
}
.recap-terms__checkbox .journey-info__checkbox-input {
  margin-top: 0.15rem;
}
.recap-terms__toggle {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0;
  background: none;
  border: 0;
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.2em;
  cursor: pointer;
}
.recap-terms__toggle:hover { color: var(--primary); }
.recap-terms__chevron {
  transition: transform var(--dur-fast) var(--ease-out);
}
.recap-terms__chevron.is-open { transform: rotate(180deg); }
.recap-terms__expanded {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  line-height: 1.55;
  color: var(--text-muted);
}
.recap-terms__expanded p { margin: 0; }
.recap-terms__expanded strong { color: var(--color-chocolate); }
.recap-terms__links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1rem;
  padding-top: 0.35rem;
  border-top: 1px solid color-mix(in oklab, var(--border) 80%, transparent);
}
.recap-terms__links a {
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.2em;
}
.recap-terms__links a:hover { color: var(--primary); }

/* Encart Club STUD'LIFE — visuellement discret (ton secondaire, pas concurrent
   de la carte produit) pour rester de la réassurance et non du contenu central. */
.recap-club {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: 1fr;
  padding: 0.85rem;
  background: var(--bg-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}
@media (min-width: 30rem) {
  .recap-club { grid-template-columns: minmax(0, 7rem) minmax(0, 1fr); align-items: center; }
}
.recap-club__media {
  overflow: hidden;
  border-radius: var(--radius-sm);
  background: var(--bg-pink-soft);
}
.recap-club__media img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 600 / 400;
  object-fit: cover;
}
.recap-club__body {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}
.recap-club__badge {
  align-self: flex-start;
  font-size: var(--text-xs);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-on-primary);
  background: var(--primary);
  padding: 0.1rem 0.5rem;
  border-radius: var(--radius-pill);
}
.recap-club__title {
  font-size: var(--text-base);
  font-weight: 800;
  color: var(--color-chocolate);
}
.recap-club__intro,
.recap-club__benefits {
  font-size: var(--text-xs);
  line-height: 1.5;
  color: var(--text-muted);
}

/* Rangée d'actions du récap : [Partager] ou [Payer] (empilée sur mobile).
 * Auto-layout maquette : la rangée remplit la largeur du formulaire (le form est
 * en colonne → étirement par défaut), boutons centrés, gap 10px. */
.recap-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
}
.recap-actions__or {
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--primary);
}

/* Boutons du récap : même hauteur (47px, maquette « Hug »). min-height plutôt
 * qu'une hauteur fixe pour qu'un libellé traduit plus long puisse grandir sans
 * être tronqué. Largeur au contenu (pas de min-width imposée). */
.recap-pay,
.recap-share {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.9375rem;
  padding-block: 0;
}

/* CTA Payer + hint d'erreur */
.recap-pay {
  padding-inline: 2rem;
}
.recap-pay[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
}

/* CTA « Partager le lien » : variante contour (orange), libellé sur deux lignes. */
.recap-share {
  flex-direction: column;
  gap: 0;
  padding-inline: 1.5rem;
  background: var(--color-white);
  border-color: var(--primary);
  color: var(--primary);
}
.recap-share:hover {
  background: color-mix(in oklab, var(--primary) 8%, white);
  border-color: var(--primary);
}
.recap-share[disabled] {
  opacity: 0.55;
  cursor: not-allowed;
}
.recap-share__main { font-size: var(--text-base); font-weight: 700; line-height: 1.1; }
.recap-share__sub { font-size: var(--text-xs); font-weight: 600; line-height: 1.1; }

@media (max-width: 30rem) {
  .recap-actions { align-self: stretch; flex-direction: column; }
  .recap-share, .recap-pay { align-self: stretch; width: 100%; }
}
.recap-pay__hint {
  color: var(--text-muted) !important;
  font-weight: 500;
  text-align: center;
  margin-top: -0.4rem;
}
.recap-pay__error {
  color: var(--danger) !important;
  font-weight: 600;
  text-align: center;
  margin-top: -0.4rem;
}
.recap-pay__error-link {
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
}
</style>
